import { Component, ElementRef, Inject, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ImageGalleryComponent } from "../../image-gallery/image-gallery.component";

import { getDictionaryItem } from "../../../utils/dictionary.domain";
import { BusinessAreaService } from "../../../services/business-area.service";
import { CategoryService } from "../../../services/category.service";
import { AnnotationService } from "../../../services/annotations.service";
import { AccountsService } from "../../../services/accounts.service";
import type { Case } from "../../../interfaces/case.interface";
import type { BusinessArea } from "../../../interfaces/business-area-ce.interface";
import type { Category } from "../../../interfaces/category.interface";
import type { Account } from "../../../interfaces/accounts.interface";
import type { Annotation } from "../../../interfaces/annotation.interface";
import { CaseService } from "../../../services/case.service";
import { Router } from "@angular/router";
import { MessageCenterComponent } from "../../message-center/message-center.component";
import { MessageBannerState, MessageCenter } from "../../../interfaces/messageCenter.interface";
import { FileToCase } from "../../../interfaces/fileToCase.interface";
import { CreateCaseService } from "../../../services/create-case.service";
import { CdkTrapFocus } from "@angular/cdk/a11y";
import { handleClipboardFiles } from "../../../utils/pasteFiles.utils";
import { CommonModule } from "@angular/common";

@Component({
    selector: "app-case-dialog",
    standalone: true,
    imports: [MatIcon, MatButton, MatProgressSpinnerModule, ImageGalleryComponent, MessageCenterComponent, CdkTrapFocus, CommonModule],
    templateUrl: "./case-dialog.component.html",
    styleUrl: "./case-dialog.component.scss"
})
export class CaseDialogComponent {
    popupId: string = "case-dialog";
    caseForm: string = "case-form";
    closeCaseDialogButton: string = "close-case-dialog-button";
    popupWarning: string = "show-close-case-warning";

    @ViewChild("caseTextArea") caseTextArea!: ElementRef;
    @ViewChild(ImageGalleryComponent) imageGallery!: ImageGalleryComponent;

    businessArea: string = "";
    category: string = "";
    account: { account: string; reportedBy: string } = {
        account: "",
        reportedBy: ""
    };
    images: Annotation[] = [];

    isLoading: boolean = true;
    showCloseWarning: boolean = false;
    isEditing: boolean = false;

    showMessageCenter: boolean = false;
    messageCenterState: MessageBannerState = MessageBannerState.success;
    messageCenter: MessageCenter = {
        title: ""
    };

    newAnnotation: { title: string; url: string }[] = [];
    removedAnnotations: Annotation[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: Case,
        public dialogRef: MatDialogRef<CaseDialogComponent>,
        private caseService: CaseService,
        businessAreaService: BusinessAreaService,
        categoryService: CategoryService,
        accountService: AccountsService,
        private annotationService: AnnotationService,
        private createCaseService: CreateCaseService,
        private router: Router
    ) {
        businessAreaService.getArea(data.businessareaId).subscribe((area: BusinessArea) => {
            if (!area) return;
            this.businessArea = area.name;
        });
        categoryService.getCategory(data.casecategoryId).subscribe((category: Category) => {
            if (!category) return;
            this.category = category.name;
        });
        accountService.getAccount(data.accountId).subscribe((account: Account) => {
            if (!account) return;
            this.account.account = account.name;
        });
        caseService.getCaseReporter(data.reportedbyId).subscribe((reporter: any) => {
            if (!reporter) return;
            this.account.reportedBy = reporter.value[0].fullname;
        });
        annotationService.getAnnotations(data.incidentid).subscribe((annotations: Annotation[]) => {
            if (annotations) {
                this.images = annotations;
                this.isLoading = false;
            } else {
                this.isLoading = false;
            }
        });
    }

    getDictionaryItem = getDictionaryItem;

    editCase() {
        if (this.isEditing === false) {
            this.isEditing = true;

            // allows the elemt to be rendered before focusing
            setTimeout(() => {
                if (this.caseTextArea) this.caseTextArea.nativeElement.focus();
            }, 100);
        } else if (this.isEditing === true) {
            this.isEditing = false;
        }
    }

    eventNewAnnotation(event: { title: string; url: string }[]) {
        this.newAnnotation = event;
    }

    eventRemoveAnnotation($event: Annotation[]) {
        this.removedAnnotations = $event;
    }

    closeCaseDialog() {
        this.dialogRef.close();
        this.router.navigate([], {
            queryParams: { caseId: null },
            queryParamsHandling: "merge"
        });
    }

    messageCenterFlow(title: string, state: MessageBannerState) {
        this.isLoading = false;
        this.isEditing = false;
        this.showMessageCenter = true;
        this.messageCenter.title = title;
        this.messageCenterState = state;

        setTimeout(() => {
            this.showMessageCenter = false;
        }, 5000);
    }

    public updateEditedCase() {
        this.isLoading = true;
        let newDescription: string | undefined = this.caseTextArea.nativeElement.value;

        this.caseService.getCase(this.data.accountId, this.data.incidentid).subscribe((caseData: Case[]) => {
            if (!caseData) {
                this.isLoading = false;
                return;
            }

            if (caseData[0].statuscode !== 922520000) {
                this.messageCenterFlow("Din sag er under behandling og kan ikke længere redigeres.", MessageBannerState.error);
                return;
            } else {
                const body = { description: newDescription };
                this.caseService.updateCase(this.data.incidentid, body).subscribe((caseData: Case) => {
                    try {
                        this.messageCenterFlow("Din sag er blevet opdateret. Siden bliver genindlæst...", MessageBannerState.success);
                        this.data.description = newDescription;

                        if (this.newAnnotation.length > 0) {
                            this.newAnnotation.forEach((annotation) => {
                                this.submitImagesToCase(annotation.url, this.data.incidentid, annotation.title);
                            });
                        }

                        if (this.removedAnnotations.length > 0) {
                            this.removedAnnotations.forEach((annotation) => {
                                this.annotationService.deleteAnnotationFromCE(annotation.annotationid).subscribe((response) => {
                                    return response;
                                });
                            });
                        }
                        if (this.removedAnnotations.length > 0 || this.newAnnotation.length > 0) {
                            setTimeout(() => {
                                location.reload();
                            }, 5000);
                        }
                        return caseData;
                    } catch {
                        this.messageCenterFlow("Der skete en fejl under redigering af sagen.", MessageBannerState.error);
                        return;
                    }
                });
            }
        });
    }

    closeCase() {
        this.isLoading = true;

        if (this.data.incidentid) {
            this.caseService.closeCaseCustomer(this.data.incidentid).subscribe((caseData: Case) => {
                if (caseData) {
                    this.isLoading = false;
                    this.router.navigate([], {
                        queryParams: { caseId: null },
                        queryParamsHandling: "merge"
                    });
                    location.reload();
                    return caseData;
                } else {
                    this.isLoading = false;
                    this.router.navigate([], {
                        queryParams: { caseId: null },
                        queryParamsHandling: "merge"
                    });
                    location.reload();

                    return;
                }
            });
        }
    }

    submitImagesToCase(imageUrl: string, caseId: string, filename: string) {
        const mappedImages: FileToCase | undefined = this.createCaseService.mapFileToCase(caseId, imageUrl, filename);
        if (mappedImages) {
            this.createCaseService.postFileToCE(mappedImages).subscribe((response) => {
                return response;
            });
        }
    }

    async pasteFilesToImageGallery(event: ClipboardEvent) {
        await handleClipboardFiles(event, (syntheticEvent) => {
            if (this.imageGallery) {
                this.imageGallery.addNewFiles(syntheticEvent);
            }
        });
    }
}
